import { normalizeMatchSnapshot, isCompletedSet } from './matchState.js';

const pair = (event) => Number.isInteger(event?.awayScore) && Number.isInteger(event?.homeScore);

// A rally tip is usable only when a continuous sequence extends an exact
// summary checkpoint. Larger numbers alone never establish a newer score.
export function reconcileRallySnapshot(summary, events = [], { correction = false } = {}) {
  events = Array.isArray(events) ? events : [];
  let match = normalizeMatchSnapshot(summary);
  const away = [...match.away.setScores], home = [...match.home.setScores];
  const notices = [];
  let accepted = events;
  for (let set = 1; set <= 5; set += 1) {
    const rows = events.filter(event => event.setNumber === set && pair(event));
    if (!rows.length) continue;
    const a = away[set - 1], h = home[set - 1], last = rows.at(-1);
    if (a === last.awayScore && h === last.homeScore) continue;
    const checkpoint = rows.findLastIndex(event => event.awayScore === a && event.homeScore === h);
    const tail = checkpoint >= 0 ? rows.slice(checkpoint + 1) : [];
    let previous = { awayScore: a, homeScore: h };
    const continuous = tail.length > 0 && tail.every(event => {
      if (isCompletedSet(previous.awayScore, previous.homeScore, set)) return false;
      const da = event.awayScore - previous.awayScore, dh = event.homeScore - previous.homeScore;
      previous = event;
      return !event.feedGapBefore && da >= 0 && dh >= 0 && da + dh === 1;
    });
    if (!correction && match.status === 'live' && !isCompletedSet(a, h, set) && continuous) {
      away[set - 1] = last.awayScore;
      home[set - 1] = last.homeScore;
      notices.push(`Set ${set}: summary feed is delayed; showing the validated rally score.`);
    } else {
      if (correction) {
        // Do not redraw superseded rallies as if a corrected score never fell.
        const keep = new Set(checkpoint >= 0 ? rows.slice(0, checkpoint + 1) : []);
        accepted = accepted.filter(event => event.setNumber !== set || keep.has(event));
      }
      notices.push(`Set ${set}: ${correction ? 'official score corrected' : 'rally feed and summary differ'}; the header uses the official summary. The graph shows only available rallies.`);
    }
  }
  match = normalizeMatchSnapshot({ ...match,
    away: { ...match.away, setScores: away }, home: { ...match.home, setScores: home } });
  return { match, events: accepted, scoreSyncNotice: notices.join(' ') };
}

export function isSummaryCorrection(previous, incoming) {
  if (!previous || !incoming?.authoritative) return false;
  const oldTime = Date.parse(previous.fetchedAt || ''), nextTime = Date.parse(incoming.fetchedAt || '');
  if (!Number.isFinite(nextTime) || (Number.isFinite(oldTime) && nextTime < oldTime)) return false;
  return ['away', 'home'].some(side => (incoming[side]?.setScores || []).some((score, index) =>
    Number.isFinite(score) && Number.isFinite(previous[side]?.setScores?.[index]) && score < previous[side].setScores[index]));
}
